import type { PlanLimits } from '@solote/contracts';

/**
 * OS DESTAQUES DO CARD SAEM DOS LIMITES DO PLANO.
 *
 * Eles eram texto livre guardado ao lado (`plan.benefits`), e o painel nunca
 * ofereceu um campo para edita-los. O resultado era inevitavel: o administrador
 * mudava "Fotos por anuncio" para 3, salvava, e o card do cliente continuava
 * anunciando "1 foto" — porque a frase era outra coisa, escrita uma vez e
 * esquecida. Os cinco planos em producao chegaram assim, cada um com uma lista
 * que repetia os proprios limites, e um deles ja discordava.
 *
 * Duas verdades sobre o mesmo numero e sempre uma a mais. A que vale e a que o
 * sistema aplica — `PlanLimits`, consultada em toda checagem de limite —, entao
 * e dela que a vitrine passa a falar. Editar o numero no painel muda o que o
 * cliente le, que era exatamente o que faltava.
 *
 * ACRESCENTAR UM LIMITE AO CONTRATO pede uma linha aqui, e uma so, valendo
 * para todos os planos. Limite em zero nao vira linha: "0 videos por anuncio"
 * ocupa espaco para dizer que nao tem.
 */

const UM_OU_MAIS = (valor: number, singular: string, plural: string) =>
  `${valor} ${valor === 1 ? singular : plural}`;

type Destaque = {
  /** Limite que nao diz nada ao cliente fica de fora do card. */
  vale: (limites: PlanLimits) => boolean;
  texto: (limites: PlanLimits) => string;
};

/*
  O texto de "ilimitado" e escrito por linha, e nao montado com o nome do
  limite. Em portugues o genero muda — "anuncios ilimitados", "fotos
  ilimitadas" —, e uma formula generica erra metade das vezes.
*/
const DESTAQUES: Destaque[] = [
  {
    vale: () => true,
    texto: (limites) =>
      limites.maxActiveListings < 0
        ? 'Anúncios ilimitados'
        : UM_OU_MAIS(limites.maxActiveListings, 'anúncio ativo', 'anúncios ativos'),
  },
  {
    vale: (limites) => limites.maxPhotosPerListing !== 0,
    texto: (limites) =>
      limites.maxPhotosPerListing < 0
        ? 'Fotos ilimitadas por anúncio'
        : `${UM_OU_MAIS(limites.maxPhotosPerListing, 'foto', 'fotos')} por anúncio`,
  },
  {
    vale: (limites) => limites.maxVideosPerListing !== 0,
    texto: (limites) =>
      limites.maxVideosPerListing < 0
        ? 'Vídeos ilimitados por anúncio'
        : `${UM_OU_MAIS(limites.maxVideosPerListing, 'vídeo', 'vídeos')} por anúncio`,
  },
  {
    vale: (limites) => limites.maxFeaturedListings !== 0,
    texto: (limites) =>
      limites.maxFeaturedListings < 0
        ? 'Destaques ilimitados'
        : UM_OU_MAIS(limites.maxFeaturedListings, 'destaque incluso', 'destaques inclusos'),
  },
  {
    vale: () => true,
    texto: (limites) => `${limites.listingDurationDays} dias de validade`,
  },
  {
    vale: (limites) => limites.maxSubUsers !== 0,
    texto: (limites) =>
      limites.maxSubUsers < 0
        ? 'Usuários da equipe ilimitados'
        : UM_OU_MAIS(limites.maxSubUsers, 'usuário da equipe', 'usuários da equipe'),
  },
  {
    vale: (limites) => limites.developmentPages,
    texto: () => 'Página de empreendimento',
  },
  {
    vale: (limites) => limites.reports.length > 0,
    texto: (limites) => (limites.reports.length === 1 ? 'Relatório de desempenho' : 'Relatórios de desempenho'),
  },
  {
    vale: (limites) => limites.rankingBoost > 0,
    texto: () => 'Posição de destaque na busca',
  },
];

/** As linhas do card, na ordem em que aparecem. */
export const destaquesDoPlano = (limites: PlanLimits): string[] =>
  DESTAQUES.filter((destaque) => destaque.vale(limites)).map((destaque) => destaque.texto(limites));
